import React, { FC } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';

import { BusinessUnitLeadMetrics } from 'services/types/metrics';

import ChartCard from './ChartCard';

interface LeadChartProps {
  leadMetrics?: BusinessUnitLeadMetrics;
}

/**
 * Lead charts component for displaying various lead metrics visualizations
 *
 * @param {Object} props - The props object
 * @param {BusinessUnitLeadMetrics} [props.leadMetrics] - Lead metrics data
 * @returns {React.ReactElement} Lead charts component
 */
const LeadCharts: FC<LeadChartProps> = ({ leadMetrics }: LeadChartProps) => {
  if (!leadMetrics) {
    return <CircularProgress />;
  }

  const sourceChartData = Object.entries(leadMetrics.chartsData.leadsBySource).map((source, index) => ({
    id: index,
    value: source[1],
    label: source[0].length > 43 ? `${source[0].slice(0, 20)}...${source[0].slice(-20)}` : source[0],
  }));

  const statusChartData = Object.entries(leadMetrics.chartsData.leadsByStatus).map((status, index) => ({
    id: index,
    value: status[1],
    label: status[0],
  }));

  return (
    <Box>
      <Grid container direction="column">
        <ChartCard
          title="Leads por día"
          description="Leads creados cada día. Los 'Leads en CRM' son los que fueron subidos al sistema CRM"
        >
          <LineChart
            series={[
              {
                data: Object.values(leadMetrics.chartsData.leadsByCreationDay),
                label: `Leads (${Object.values(leadMetrics.chartsData.leadsByCreationDay).reduce((a, b) => a + b, 0)})`,
              },
              {
                data: Object.values(leadMetrics.chartsData.leadsUploadedToCRMByCreationDay),
                label: `Leads en CRM (${Object.values(leadMetrics.chartsData.leadsUploadedToCRMByCreationDay).reduce(
                  (a, b) => a + b,
                  0
                )})`,
                valueFormatter: (value: number | null, { dataIndex }: { dataIndex: number }) => {
                  return `${value} (${(
                    ((value || 0) * 100) /
                    (Object.values(leadMetrics.chartsData.leadsByCreationDay)[dataIndex] || 1)
                  ).toFixed(1)}%)`;
                },
              },
            ]}
            height={400}
            xAxis={[
              {
                scaleType: 'band',
                data: Object.keys(leadMetrics.chartsData.leadsByCreationDay),
                tickLabelStyle: {
                  angle: 45,
                  textAnchor: 'start',
                  fontSize: 12,
                },
              },
            ]}
            yAxis={[
              {
                label: 'Cantidad de leads',
                min: 0,
                tickMinStep: 1,
              },
            ]}
            margin={{ top: 10, bottom: 60, left: 40, right: 80 }}
            slotProps={{
              legend: {
                position: {
                  vertical: 'top',
                  horizontal: 'right',
                },
              },
            }}
          />
        </ChartCard>

        <ChartCard title="Leads por hora de creación" description="Distribución de leads por hora del día">
          <BarChart
            series={[
              {
                data: Object.values(leadMetrics.chartsData.leadsByCreationHour),
                label: 'Leads',
              },
            ]}
            height={300}
            xAxis={[
              {
                scaleType: 'band',
                data: Object.keys(leadMetrics.chartsData.leadsByCreationHour).map((hour) => `${hour}h`),
              },
            ]}
            yAxis={[
              {
                label: 'Cantidad de leads',
                min: 0,
                tickMinStep: 1,
              },
            ]}
            margin={{ top: 10, bottom: 60, left: 40, right: 80 }}
          />
        </ChartCard>

        <ChartCard title="Leads por origen" description="Distribución de leads por fuente de origen">
          <PieChart
            skipAnimation
            series={[
              {
                data: sourceChartData.map((item) => ({
                  ...item,
                  label: `${item.label} (${item.value})`,
                })),
              },
            ]}
            height={(Math.floor(sourceChartData.length / 5) + 1) * 200}
          />
        </ChartCard>

        <ChartCard title="Leads por estado" description="Distribución de leads por estado actual">
          <PieChart
            skipAnimation
            series={[
              {
                data: statusChartData.map((item) => ({
                  ...item,
                  label: `${item.label} (${item.value})`,
                })),
              },
            ]}
            height={(Math.floor(statusChartData.length / 5) + 1) * 200}
          />
        </ChartCard>
      </Grid>
    </Box>
  );
};

LeadCharts.defaultProps = {
  leadMetrics: undefined,
};

export default LeadCharts;
